import { Controller } from "@hotwired/stimulus"
import { productsFailure } from "../lib/checkout_status"

const PRODUCTS_RETRY_DELAY = 2000
// Long enough that a slow App Store sheet is not mistaken for a lost one.
const PURCHASE_TIMEOUT = 120000

// The StoreKit subscription paywall. Same bridge contract as the credits
// checkout (app_store_checkout_controller): request ids pair replies with
// requests, and a purchase is only considered done when the native side says
// the server accepted it.
export default class extends Controller {
  static targets = ["plan", "status"]
  static values = { messages: { type: Object, default: {} } }

  connect() {
    this.connected = true
    this.productsRetried = false
    this.productsListener = (event) => this.handleProducts(event)
    this.purchaseListener = (event) => this.handlePurchase(event)
    window.addEventListener("dropkind:iap-products", this.productsListener)
    window.addEventListener("dropkind:iap-purchase", this.purchaseListener)

    this.loadProducts()
  }

  disconnect() {
    this.connected = false
    this.clearPurchaseTimeout()
    window.removeEventListener("dropkind:iap-products", this.productsListener)
    window.removeEventListener("dropkind:iap-purchase", this.purchaseListener)
  }

  loadProducts() {
    const bridge = window.webkit?.messageHandlers?.loadSubscriptionProducts
    if (!bridge) return this.showStatus(this.message("unavailable"))

    this.productsRequestId = this.requestId()
    bridge.postMessage({ requestId: this.productsRequestId })
  }

  purchase(event) {
    const bridge = window.webkit?.messageHandlers?.purchaseSubscription
    if (!bridge || this.purchaseRequestId) return

    this.purchaseRequestId = this.requestId()
    this.startPurchaseTimeout()
    this.setBusy(true)
    this.showStatus(this.message("purchasing"))
    this.track("subscription_checkout_opened")
    bridge.postMessage({ requestId: this.purchaseRequestId, productId: event.currentTarget.dataset.productId })
  }

  restore() {
    const bridge = window.webkit?.messageHandlers?.restoreSubscriptions
    if (!bridge || this.purchaseRequestId) return

    this.purchaseRequestId = this.requestId()
    this.startPurchaseTimeout()
    this.setBusy(true)
    this.showStatus(this.message("restoring"))
    bridge.postMessage({ requestId: this.purchaseRequestId })
  }

  // If the native side is killed mid-purchase the bridge event never arrives and the plan
  // buttons stay disabled with no way out but a reload. Letting go of the UI is safe: StoreKit
  // still replays the transaction through Transaction.updates, so a real purchase is not lost.
  startPurchaseTimeout() {
    this.clearPurchaseTimeout()
    this.purchaseTimeout = window.setTimeout(() => {
      if (!this.connected || !this.purchaseRequestId) return

      this.purchaseRequestId = null
      this.showStatus(this.message("purchase_timed_out"))
      this.setBusy(false)
    }, PURCHASE_TIMEOUT)
  }

  clearPurchaseTimeout() {
    if (this.purchaseTimeout) window.clearTimeout(this.purchaseTimeout)
    this.purchaseTimeout = null
  }

  handleProducts(event) {
    if (!this.connected || event.detail?.requestId !== this.productsRequestId) return
    if (event.detail.status !== "loaded") return this.handleProductsFailure(event.detail.status)

    const products = new Map(event.detail.products.map((product) => [product.productId, product]))
    this.planTargets.forEach((plan) => {
      const product = products.get(plan.dataset.productId)
      if (!product) return
      plan.querySelector("[data-app-store-price]").textContent = product.displayPrice
      plan.disabled = false
    })
    this.showStatus("")
  }

  handleProductsFailure(status) {
    const outcome = productsFailure(status, { retried: this.productsRetried })
    if (outcome.action === "show") return this.showStatus(this.message(outcome.messageKey))

    this.productsRetried = true
    window.setTimeout(() => { if (this.connected) this.loadProducts() }, PRODUCTS_RETRY_DELAY)
  }

  handlePurchase(event) {
    if (!this.connected || event.detail?.requestId !== this.purchaseRequestId) return
    this.clearPurchaseTimeout()
    this.purchaseRequestId = null

    switch (event.detail.status) {
    case "fulfilled":
    case "duplicate":
      this.track("subscription_checkout_completed")
      this.showStatus(this.message("completed"))
      // The refreshed page renders the subscription state card.
      window.setTimeout(() => window.Turbo?.visit(window.location.href, { action: "replace" }), 500)
      break
    case "pending":
      this.showStatus(this.message("pending"))
      this.setBusy(false)
      break
    case "cancelled":
      this.showStatus("")
      this.setBusy(false)
      break
    default:
      this.showStatus(this.message(event.detail.status))
      this.setBusy(false)
    }
  }

  setBusy(busy) {
    this.planTargets.forEach((plan) => { plan.disabled = busy })
  }

  showStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message || ""
  }

  message(key) {
    return this.messagesValue[key] || this.messagesValue.unavailable || ""
  }

  requestId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`
  }

  track(event) {
    if (typeof window.gtag === "function") window.gtag("event", event)
  }
}
